import { useTranslation } from "react-i18next";

import type { PaymentStatus, PaymentRead } from "@/api-v3";
import { ErrorRetryComponent } from "@/components/error-card/error-retry";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { formatISODate } from "@/lib/date-formatter";

import { getPaymentDisplayAmount } from "../../../helpers/get-payment-display-amount";
import { getPaymentTitle } from "../../../helpers/get-payment-title";
import { raisedByStudyTeam } from "../../../helpers/raised-by-study-team";

import { PaymentListItemComponent } from "./payment-list-item";
import { PaymentListItemSkeleton } from "./payment-list-item-skeleton";

const SKELETON_COUNT = 3;

export interface PaymentListProps {
  payments: PaymentRead[] | undefined;
  isLoading: boolean;
  isErrored: boolean;
  hasNextPage: boolean;
  onFetchMorePayments: () => void;
  onRefetchMorePayments?: () => void;
  showEndOfHistoryMessage?: boolean;
}

export function PaymentListComponent({
  payments,
  isLoading,
  isErrored,
  hasNextPage,
  onFetchMorePayments,
  onRefetchMorePayments = onFetchMorePayments,
  showEndOfHistoryMessage = true,
}: PaymentListProps) {
  const { t } = useTranslation();
  const { containerRef, triggerRef } = useInfiniteScroll({
    allowGetter: !isLoading && hasNextPage && !isErrored,
    observerAreaSize: "200px",
    getter: onFetchMorePayments,
  });

  const isPaymentsLoading = (isLoading || payments == null) && !isErrored;

  if (payments?.length === 0 && !hasNextPage && !isPaymentsLoading) {
    return <div className="text-center">{t("payments_empty")}</div>;
  }

  return (
    <div
      ref={containerRef}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start"
    >
      {payments?.map((payment) => (
        <PaymentListItemComponent
          id={payment.id}
          title={getPaymentTitle(payment.type, t)}
          subtitle={formatISODate(payment.created_at, { format: "short-date" })}
          key={payment.id}
          amount={getPaymentDisplayAmount(payment)}
          currency={payment.currency}
          // TODO: remove cast once API status is generated as PaymentStatus
          status={payment.status as PaymentStatus}
          type={payment.type}
          payoutStatus={payment.payout_status}
          raisedByStudyTeam={raisedByStudyTeam(payment)}
        />
      ))}
      {isPaymentsLoading &&
        Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <PaymentListItemSkeleton key={`skeleton-${index}`} />
        ))}
      {isErrored && (
        <div className="col-span-full flex justify-center gap-3">
          <ErrorRetryComponent
            retry={onRefetchMorePayments}
            direction="vertical"
          />
        </div>
      )}
      {hasNextPage ? (
        <div ref={triggerRef} className="col-span-full" />
      ) : (
        showEndOfHistoryMessage && (
          <div className="col-span-full w-full text-center mt-2 text-sm text-muted-foreground">
            {t("payments_endOfHistory")}
          </div>
        )
      )}
    </div>
  );
}
